import { useState, type FormEvent } from 'react';
import { siteInfo } from '@/mocks/site';

const FORM_ID = 'dah8llroh653ivfvnodg';
const FORM_ACTION = 'https://readdy.ai/api/form/dah8llroh653ivfvnodg';

const topics = ['提供核对线索', '指出档案错误', '内容与转载咨询', '其他'];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const trap = String(formData.get('contact_alt') ?? '').trim();
    if (trap) {
      setStatus('success');
      return;
    }
    if (formData.get('contact_alt') !== null) formData.delete('contact_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam ? '提交未通过校验，请检查内容后重试。' : serverMsg || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，未能提交，请稍后重试。');
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <p className="font-label text-xs tracking-[0.25em] text-primary-600 mb-3">
              CONTACT / 联系我们
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              有线索，或者发现我们写错了
            </h2>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              欢迎提供更完整的一手信息，也欢迎指出档案中的疏漏。每一条有效反馈我们都会重新核对，并在档案中标注修改日期。
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">邮箱</span>
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="text-sm text-foreground-900 hover:text-primary-600 transition-colors break-all"
                  >
                    {siteInfo.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">处理时间</span>
                  <span className="text-sm text-foreground-900">{siteInfo.workTime}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-information-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">说明</span>
                  <span className="text-sm text-foreground-900">
                    我们不接受删改真实判定的请求，但欢迎补充信源
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-8">
            <div className="p-5 md:p-8 rounded-lg bg-background-100 border border-background-200">
              {status === 'success' ? (
                <div className="py-12 flex flex-col items-center text-center">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-checkbox-circle-line text-3xl"></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                    已收到，谢谢你的反馈
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 max-w-md">
                    我们会在 {siteInfo.workTime} 内查看。如果信息有效，会更新对应档案并标注修改日期。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-5 py-3 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    再提交一条
                  </button>
                </div>
              ) : (
                <form id={`form-${FORM_ID}`} data-readdy-form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="ct-name" className="block text-sm text-foreground-800 mb-2">
                        称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="ct-name"
                        type="text"
                        name="name"
                        required
                        placeholder="怎么称呼你"
                        className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="ct-email" className="block text-sm text-foreground-800 mb-2">
                        联系邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="ct-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="ct-topic" className="block text-sm text-foreground-800 mb-2">
                      反馈类型
                    </label>
                    <select
                      id="ct-topic"
                      name="topic"
                      defaultValue={topics[0]}
                      className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                    >
                      {topics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="ct-message" className="block text-sm text-foreground-800 mb-2">
                      具体内容 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="ct-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请写明涉及哪部剧、哪一个判定，以及你掌握的信息来源（不超过 500 字）"
                      className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                    ></textarea>
                    <p className="mt-1.5 text-xs text-foreground-400">字数上限 500 字</p>
                  </div>

                  <input
                    type="text"
                    name="contact_alt"
                    className="form-verify-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="mt-6 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        提交中
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base"></i>
                        提交反馈
                      </>
                    )}
                  </button>

                  {status === 'error' && (
                    <p role="alert" className="mt-3 text-xs text-primary-700">
                      {formError}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}